import React, { useState, useEffect, useRef, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBinoculars, faEye, faCopy, faRefresh, faPaperPlane, faCircleInfo, faSearch, faClose } from '@fortawesome/free-solid-svg-icons'
import { useRequest } from '../../../hooks/UseRequest'
import { useResendCooldown } from '../../../hooks/UseResendCooldown'
import { setNotification } from '../../../redux/notificationSlice'
import { selectDropdownOptions } from '../../../redux/dropdownSlice'
import { selectAuth } from '../../../redux/authSlice'
import { selectUser } from '../../../redux/userSlice'
import { selectModules } from '../../../redux/moduleSlice'
import { fetchModuleData, selectModuleData } from '../../../redux/moduleDataSlice'
import { hasUserPermission } from '../../../utils/permissionHelper'
import LayoutSidebar from '../../../components/LayoutSidebar'
import Loading from '../../../components/Loading'
import Pagination from '../../../components/Pagination'
import Select from 'react-select'
import { reactSelectClassNames } from '../../../helpers/react-select-styles'
import { FormatISODateTime } from '../../../helpers/formatting'
import { concatFiltersOnUrl } from '../../../utils/listViewHelper'
import { handleToolTipMouseEnter } from '../../../helpers/layout'
import { IconWithHelperText } from '../../../components/TableRow'
import ManagerApprovalIcons from '../../../components/elements/ManagerApprovalIcons'

export default function DecisionLogicApprovalQueue() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const request = useRequest()
  const auth = useSelector(selectAuth)
  const active = useSelector(selectUser)
  const dropdownOptions = useSelector(selectDropdownOptions)
  const modules = useSelector(selectModules)
  const moduleData = useSelector(selectModuleData)

  const [module, setModule] = useState(null)
  const [loaded, setLoaded] = useState(false)
  const [partialLoad, setPartialLoad] = useState(false)
  const [searchKeyWords, setSearchKeyWords] = useState('')
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false)
  const [advancedFilters, setAdvancedFilters] = useState({
    approvalStatus: null,
    customerFirstName: '',
    customerLastName: '',
    emailAddress: '',
    requestCode: ''
  })

  const [refreshingRequestId, setRefreshingRequestId] = useState(null)
  const [resendingRequestId, setResendingRequestId] = useState(null)
  const [copyingRequestId, setCopyingRequestId] = useState(null)
  const isRefreshingRef = useRef(false)
  const resendingRef = useRef(false)
  const { startCooldown, isCoolingDown } = useResendCooldown()
  const hasInitialFetchRef = useRef(false)
  const hasFetchedSuccessfullyRef = useRef(false)
  const hadSearchKeyWordsRef = useRef(false)
  const searchTimeoutRef = useRef(null)
  const defaultResultsRef = useRef(null)
  const hasStoredDefaultRef = useRef(false)

  const processingStatusOptions = useMemo(() => {
    if (!dropdownOptions) return []
    return Object.values(dropdownOptions).filter(
      (o) => o?.code_key?.startsWith('processing_status') ||
        o?.code_key?.startsWith('bank_transaction_provider_call_outcome')
    )
  }, [dropdownOptions])

  const getStatusLabel = (request) => {
    if (request.mgr_approval_status_ddo?.name) {
      return request.mgr_approval_status_ddo.name
    }
    if (request.mgr_approval_status_dropdown_option_id && dropdownOptions) {
      const option = Object.values(dropdownOptions).find((opt) => opt?.id === request.mgr_approval_status_dropdown_option_id)
      if (option) return option.name || 'Unknown'
    }
    return '-'
  }

  const getProviderOption = (request) => {
    if (request.provider_ddo) return request.provider_ddo
    if (request.provider_dropdown_option_id && dropdownOptions) {
      return Object.values(dropdownOptions).find((opt) => opt?.id === request.provider_dropdown_option_id) || null
    }
    return null
  }

  const getDLProcessingStatus = (request) => {
    const ddo = Object.values(processingStatusOptions).find((o) => o?.id === request?.provider_calls[0]?.outcome_status_dropdown_option_id);
    if (ddo?.name) {
      const codeKey = ddo.code_key
      const statusMap = {
        processing_status_pending: 'Not Started',
        processing_status_processing: 'Started',
        processing_status_completed: 'Completed',
        bank_transaction_provider_call_outcome_decisionlogic_unsupported_bank: 'Unsupported Bank',
        bank_transaction_provider_call_outcome_decisionlogic_approved: 'Approved',
        bank_transaction_provider_call_outcome_decisionlogic_declined: 'Declined'
      }
      return statusMap[codeKey] || ddo.name || 'Unknown'
    }
    return 'Unknown'
  }

  useEffect(() => {
    const bankTransactionModule = modules?.data?.find?.((m) => m?.code_key === 'bank_transaction')
    if (bankTransactionModule) setModule(bankTransactionModule)
  }, [modules.data])

  useEffect(() => {
    if (active.status === 'succeeded') {
      const activePermissions = active.spoofed_user?.user_permission_options || active.data.user_permission_options
      if (!hasUserPermission(activePermissions, 'manager_approval_read')) {
        navigate('/403')
      }
    }
  }, [active, navigate])

  const approvalStatusOptions = useMemo(() => {
    if (!dropdownOptions) return []
    return Object.values(dropdownOptions)
      .filter((o) => o?.code_key?.startsWith('mgr_approval_status'))
      .map((o) => ({ label: o.name, value: o.id }))
  }, [dropdownOptions])

  const buildQuery = (keywordSearch = '', advancedFiltersObj = null) => {
    const filters = []

    if (keywordSearch?.trim()) {
      const keyword = keywordSearch.trim()
      filters.push({
        or: [
          { column: 'customers.first_name', value: `${keyword}%`, operator: 'like' },
          { column: 'customers.last_name', value: `${keyword}%`, operator: 'like' },
          { column: 'customers.email_address', value: `${keyword}%`, operator: 'like' }
        ]
      })
    }

    const advFilters = advancedFiltersObj || advancedFilters
    if (advFilters.approvalStatus) {
      filters.push({ or: [{ column: 'mgr_approval_status_dropdown_option_id', value: advFilters.approvalStatus, operator: '=' }] })
    }
    if (advFilters.customerFirstName?.trim()) {
      filters.push({ column: 'customers.first_name', value: advFilters.customerFirstName.trim(), operator: '=' })
    }
    if (advFilters.customerLastName?.trim()) {
      filters.push({ column: 'customers.last_name', value: advFilters.customerLastName.trim(), operator: '=' })
    }
    if (advFilters.emailAddress?.trim()) {
      filters.push({ column: 'customers.email_address', value: advFilters.emailAddress.trim(), operator: '=' })
    }
    if (advFilters.requestCode?.trim()) {
      filters.push({ column: 'ext_request_code', value: advFilters.requestCode.trim(), operator: '=' })
    }

    return { and: filters }
  }

  const fetchData = (keywordSearch = '', advancedFiltersObj = null, includePendingApproval = false) => {
    if (!module || !auth.accessToken) return

    setPartialLoad(false)
    setLoaded(false)

    let query = buildQuery(keywordSearch, advancedFiltersObj)

    if (includePendingApproval && dropdownOptions) {
      const approvalRequiredOption = Object.values(dropdownOptions).find(
        (option) => option?.code_key === 'mgr_approval_status_required'
      )
      if (approvalRequiredOption) {
        query.and.push({
          or: [{ column: 'mgr_approval_status_dropdown_option_id', value: approvalRequiredOption.id.toString(), operator: '=' }]
        })
      }
    }

    let url = `${module.code_key}?filters=${JSON.stringify(query)}`
    if (active.filtered_portfolio) {
      url = concatFiltersOnUrl(url, null, null, { column: 'portfolio_id', value: active.filtered_portfolio, operator: '=' })
    }

    dispatch(
      fetchModuleData({
        accessToken: auth.accessToken,
        module_endpoint: url,
        spoofedUserId: active.spoofed_user?.id || null
      })
    ).then(({ error }) => {
      if (error) {
        if (error.message.includes('status code 401')) {
          navigate('/403')
        } else if (error.message.includes('status code 404')) {
          navigate('/401')
        } else {
          dispatch(setNotification({ type: 'error', body: error.message }))
        }
      }
    })
  }

  const isDecisionLogicQueuePage = location.pathname === '/decision_logic_approval_queue'

  useEffect(() => {
    if (!isDecisionLogicQueuePage || !module || !auth.accessToken || !dropdownOptions || hasInitialFetchRef.current) return

    hasInitialFetchRef.current = true
    hasStoredDefaultRef.current = false
    defaultResultsRef.current = null
    // Always open with pending approval filter (1540) by default
    fetchData('', null, true)
  }, [isDecisionLogicQueuePage, auth.accessToken, module, dropdownOptions])

  useEffect(() => {
    if (isDecisionLogicQueuePage) return
    hasInitialFetchRef.current = false
    hasFetchedSuccessfullyRef.current = false
    hadSearchKeyWordsRef.current = false
    hasStoredDefaultRef.current = false
    defaultResultsRef.current = null
  }, [isDecisionLogicQueuePage, location.pathname])

  useEffect(() => {
    // Don't run on initial mount - wait for initial fetch to complete first
    if (showAdvancedSearch || !module || !auth.accessToken || !hasInitialFetchRef.current || !hasFetchedSuccessfullyRef.current) return

    const isInitialState = !searchKeyWords &&
      !advancedFilters.approvalStatus &&
      !advancedFilters.customerFirstName &&
      !advancedFilters.customerLastName &&
      !advancedFilters.emailAddress &&
      !advancedFilters.requestCode

    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current)

    if (isInitialState) {
      if (hadSearchKeyWordsRef.current) {
        hadSearchKeyWordsRef.current = false
        fetchData('', null, true)
      }
      return
    }

    if (searchKeyWords?.trim()) hadSearchKeyWordsRef.current = true
    if (searchKeyWords?.trim() != '') {
      searchTimeoutRef.current = setTimeout(() => {
        fetchData(searchKeyWords, advancedFilters, false)
      }, 1000)
    }

    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current)
    }
  }, [searchKeyWords, showAdvancedSearch, module, auth.accessToken, advancedFilters])

  useEffect(() => {
    if (moduleData.status === 'succeeded' && hasInitialFetchRef.current) {
      setLoaded(true)
      setPartialLoad(true)
      hasFetchedSuccessfullyRef.current = true
      const isBankTransactionData = Array.isArray(moduleData.data) &&
        (moduleData.data.length === 0 || (moduleData.data[0] && Array.isArray(moduleData.data[0].provider_calls)))
      if (!hasStoredDefaultRef.current && moduleData.data && isBankTransactionData) {
        defaultResultsRef.current = { data: moduleData.data, meta: moduleData.meta || {} }
        hasStoredDefaultRef.current = true
      }
    }
  }, [moduleData])

  const copyToClipboard = async (link, requestId) => {
    setCopyingRequestId(requestId)
    try {
      await navigator.clipboard.writeText(link)
      dispatch(setNotification({ type: 'success', body: 'Link copied to clipboard!' }))
    } catch (error) {
      const textArea = document.createElement('textarea')
      textArea.value = link
      document.body.appendChild(textArea)
      textArea.select()
      document.execCommand('copy')
      document.body.removeChild(textArea)
      dispatch(setNotification({ type: 'success', body: 'Link copied to clipboard!' }))
    } finally {
      setTimeout(() => setCopyingRequestId(null), 500)
    }
  }

  const refreshSingleRequest = async (providerCallId) => {
    if (!providerCallId) return

    setRefreshingRequestId(providerCallId)
    isRefreshingRef.current = true
    try {
      const currentRequest = moduleData.data?.find((item) => item.provider_calls?.some((pc) => pc.id === providerCallId))

      const endpoint = currentRequest?.customer_bank_account_id
        ? `${process.env.REACT_APP_API_DOMAIN}/bank_account/${currentRequest.customer_bank_account_id}/bank_transaction/decision_logic_request_status/${providerCallId}?refresh=true`
        : `${process.env.REACT_APP_API_DOMAIN}/bank_transaction_provider_call/${providerCallId}/decision_logic_request_status?refresh=true`

      request.setEndpoint(endpoint)
      const response = await request.get()

      if (response?.data?.data) {
        const updatedPayload = response.data.data
        const isFullBankTransaction = Array.isArray(updatedPayload?.provider_calls)
        const updatedData = (moduleData.data || []).map((item) => {
          const providerCalls = item.provider_calls || []
          const idx = providerCalls.findIndex((pc) => pc.id === providerCallId)
          if (idx === -1) return item
          if (isFullBankTransaction) return updatedPayload
          const updatedCall = { ...providerCalls[idx], ...updatedPayload }
          if (dropdownOptions && updatedCall.processing_status_dropdown_option_id != null) {
            const ddo = Object.values(dropdownOptions).find(
              (o) => o?.id === updatedCall.processing_status_dropdown_option_id
            )
            if (ddo) updatedCall.processing_status_ddo = ddo
          }
          if (dropdownOptions && updatedCall.outcome_status_dropdown_option_id != null) {
            const outcomeDdo = Object.values(dropdownOptions).find(
              (o) => o?.id === updatedCall.outcome_status_dropdown_option_id
            )
            if (outcomeDdo) updatedCall.outcome_status_ddo = outcomeDdo
          }
          const newCalls = [...providerCalls]
          newCalls[idx] = updatedCall
          return { ...item, provider_calls: newCalls }
        })
        dispatch(fetchModuleData({
          preloadedData: {
            data: updatedData,
            meta: moduleData.meta || {}
          }
        }))
      }
    } catch (error) {
      dispatch(setNotification({
        type: 'error',
        body: error?.response?.data?.message || 'Failed to refresh request status.'
      }))
    } finally {
      setTimeout(() => {
        isRefreshingRef.current = false
        setRefreshingRequestId(null)
      }, 0)
    }
  }

  const resendDecisionLogicRequest = async (requestId, customerId) => {
    if (!requestId || resendingRef.current || isCoolingDown(customerId)) return
    resendingRef.current = true
    setResendingRequestId(requestId)
    try {
      request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/bank_transaction_provider_call/${requestId}/decision_logic_resend_email`)
      const response = await request.post({})
      if (response?.data?.success) {
        startCooldown(customerId)
      } else {
        dispatch(setNotification({
          type: 'error',
          body: response?.data?.message || 'Failed to resend DecisionLogic request.'
        }))
      }
    } catch (error) {
      dispatch(setNotification({
        type: 'error',
        body: error?.response?.data?.message || 'Failed to resend DecisionLogic request.'
      }))
    } finally {
      resendingRef.current = false
      setResendingRequestId(null)
    }
  }

  const clearAdvancedFilters = () => {
    setAdvancedFilters({
      approvalStatus: null,
      customerFirstName: '',
      customerLastName: '',
      emailAddress: '',
      requestCode: ''
    })
  }

  const renderActions = (req, status, canViewResults, decisionLogicLink, resultsPath) => {
    if (resendingRequestId === req.id || refreshingRequestId === req.id) {
      return (
        <div className="lox-loading-container">
          <Loading size="is-size-7" />
        </div>
      )
    }

    if (canViewResults) {
      return (
        <>
          <span
            className="is-clickable"
            onClick={() => window.open(resultsPath, '_blank', 'noopener,noreferrer')}
            title="View results"
          >
            <FontAwesomeIcon icon={faEye} />
          </span>
          <span
            className="is-clickable"
            onClick={() => copyToClipboard(decisionLogicLink, req.id)}
            title="Copy link to clipboard"
          >
            <FontAwesomeIcon icon={faCopy} />
          </span>
        </>
      )
    } else {
      return (
        <>
          <button
            type="button"
            className="button is-white is-small m-0"
            onClick={() => resendDecisionLogicRequest(req.id, req.customer_id)}
            disabled={resendingRequestId !== null || isCoolingDown(req.customer_id)}
            title={isCoolingDown(req.customer_id) ? 'Email sent recently' : 'Resend email'}
          >
            <FontAwesomeIcon icon={faPaperPlane} />
          </button>
          <span
            className="is-clickable"
            onClick={() => refreshSingleRequest(req.id)}
            title="Refresh status"
          >
            <FontAwesomeIcon
              icon={faRefresh}
              className={refreshingRequestId === req.id ? 'fa-pulse' : ''}
            />
          </span>
        </>
      )
    }
  }

  return (
    <LayoutSidebar
      contentTitle="Bank Change Verification Approval Queue"
      content={(
        <>
          <div className="lox-table-filtering notification p-2 m-0">
            {!showAdvancedSearch && (
              <div className="level">
                <div className="field is-12 has-addons" style={{ width: '100%' }}>
                  <div className="control has-icons-left has-icons-right" style={{ width: '100%' }}>
                    <input
                      className="input"
                      type="text"
                      value={searchKeyWords || ''}
                      placeholder="Search by Customer Name or Email Address"
                      onChange={(e) => setSearchKeyWords(e.target.value)}
                    />
                    <span className="icon is-small is-left">
                      <FontAwesomeIcon icon={faSearch} />
                    </span>
                    {searchKeyWords && (
                      <span
                        className="icon is-small is-right is-clickable"
                        onClick={() => {
                          setSearchKeyWords('')
                          fetchData('', advancedFilters, true)
                        }}
                      >
                        <FontAwesomeIcon icon={faClose} />
                      </span>
                    )}
                  </div>
                  <div className="control">
                    <a
                      className="button has-text-link px-4"
                      onClick={() => {
                        setShowAdvancedSearch(true)
                        clearAdvancedFilters()
                      }}
                    >
                      Advanced Search
                    </a>
                  </div>
                </div>
              </div>
            )}

            {showAdvancedSearch && (
              <form id="search_form">
                <div className="columns is-multiline pb-0" style={{ zIndex: 15, position: 'relative' }}>
                  <div className="is-one-quarter-widescreen is-one-third-desktop is-half-tablet column pb-0">
                    <p className="has-text-grey is-small is-size-8">
                      <span className="mr-1">Approval Status</span>
                    </p>
                    <div>
                      <Select
                        className="react-select-container"
                        classNamePrefix="react-select"
                        classNames={reactSelectClassNames}
                        options={approvalStatusOptions}
                        value={approvalStatusOptions.find((opt) => opt.value === advancedFilters.approvalStatus) || null}
                        onChange={(selected) => setAdvancedFilters({ ...advancedFilters, approvalStatus: selected?.value || null })}
                        isClearable
                        isSearchable={false}
                        placeholder="Select Approval Status"
                        styles={{ container: (base) => ({ ...base, width: '100%' }), menu: ({ ...css }) => ({ ...css, width: '100%' }) }}
                        menuPosition="fixed"
                        form="search_form"
                      />
                    </div>
                  </div>
                  <div className="is-one-quarter-widescreen is-one-third-desktop is-half-tablet column pb-0">
                    <p className="has-text-grey is-small is-size-8">
                      <span className="mr-1">Customer First Name</span>
                    </p>
                    <div>
                      <input
                        className="input is-small"
                        type="text"
                        form="search_form"
                        value={advancedFilters.customerFirstName}
                        onChange={(e) => setAdvancedFilters({ ...advancedFilters, customerFirstName: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="is-one-quarter-widescreen is-one-third-desktop is-half-tablet column pb-0">
                    <p className="has-text-grey is-small is-size-8">
                      <span className="mr-1">Customer Last Name</span>
                    </p>
                    <div>
                      <input
                        className="input is-small"
                        type="text"
                        form="search_form"
                        value={advancedFilters.customerLastName}
                        onChange={(e) => setAdvancedFilters({ ...advancedFilters, customerLastName: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="is-one-quarter-widescreen is-one-third-desktop is-half-tablet column pb-0">
                    <p className="has-text-grey is-small is-size-8">
                      <span className="mr-1">Email Address</span>
                    </p>
                    <div>
                      <input
                        className="input is-small"
                        type="text"
                        form="search_form"
                        value={advancedFilters.emailAddress}
                        onChange={(e) => setAdvancedFilters({ ...advancedFilters, emailAddress: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="is-one-quarter-widescreen is-one-third-desktop is-half-tablet column pb-0">
                    <p className="has-text-grey is-small is-size-8">
                      <span className="mr-1">DecisionLogic Request Code</span>
                    </p>
                    <div>
                      <input
                        className="input is-small"
                        type="text"
                        form="search_form"
                        value={advancedFilters.requestCode}
                        onChange={(e) => setAdvancedFilters({ ...advancedFilters, requestCode: e.target.value })}
                      />
                    </div>
                  </div>
                </div>
                <div className="lox-form-button-wrapper has-text-right">
                  <button
                    type="button"
                    className="button has-background-grey has-text-white is-small"
                    onClick={() => {
                      setShowAdvancedSearch(false)
                      clearAdvancedFilters()
                      setSearchKeyWords('')
                      if (defaultResultsRef.current) {
                        dispatch(fetchModuleData({
                          preloadedData: defaultResultsRef.current
                        }))
                      } else {
                        fetchData('', null, true)
                      }
                    }}
                  >
                    <span className="mx-3">Cancel</span>
                  </button>
                  <button
                    type="button"
                    className="button has-background-grey has-text-white is-small"
                    onClick={clearAdvancedFilters}
                  >
                    <span className="mx-3">Clear</span>
                  </button>
                  <button
                    type="button"
                    className="button is-link has-text-white is-small"
                    onClick={() => {
                      fetchData(searchKeyWords, advancedFilters, false)
                    }}
                  >
                    <span className="mx-3">Search</span>
                  </button>
                </div>
              </form>
            )}
          </div>

          {!loaded && !partialLoad ? (
            <Loading />
          ) : (
            <div className="lox-hooktable mb-0 position-relative">
              {!moduleData.data || moduleData.data.length === 0 ? (
                <div className="has-text-centered py-6">
                  <p className="has-text-grey">No bank change verification requests found.</p>
                </div>
              ) : (
                <>
                  <table className="is-bordered lox-hooktable-table table is-striped is-narrow is-hoverable is-fullwidth is-info has-sticky-column has-view-button">
                    <thead>
                      <tr className="is-selected">
                        <th className={`is-selected sticky has-text-centered`} style={{ width: '35px' }}>
                          <span className={`button is-ghost has-text-white is-not-cursor-pointer`}>
                            <FontAwesomeIcon icon={faBinoculars} />
                          </span>
                        </th>
                        <th className="is-selected has-text-centered">ID</th>
                        <th className="is-selected has-text-centered is-nowrap">Customer</th>
                        <th className="is-selected has-text-centered is-nowrap">Request #</th>
                        <th className="is-selected has-text-centered is-nowrap">Provider</th>
                        <th className="is-selected has-text-centered is-nowrap">Approval Status</th>
                        <th className="is-selected has-text-centered is-nowrap">Action Reason</th>
                        <th className="is-selected has-text-centered is-nowrap">Bank Login Status</th>
                        <th className="is-selected has-text-centered is-nowrap">Agent</th>
                        <th className="is-selected has-text-centered is-nowrap">Created At</th>
                        <th className="is-selected has-text-centered is-nowrap">Created By</th>
                        <th className="is-selected has-text-centered is-nowrap">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(moduleData.data || [])
                        .filter((item) => item.provider_calls?.length > 0)
                        .map((item) => {
                        const providerCall = item.provider_calls[0]
                        const managerApproval = item.manager_approval || {}
                        const agentUser = managerApproval.agent_user || {}

                        const req = {
                          ...item,
                          id: providerCall.id || item.id,
                          ext_request_code: providerCall.ext_request_code || null,
                          provider_dropdown_option_id: providerCall.provider_dropdown_option_id || null,
                          provider_ddo: providerCall.provider_ddo || null,
                          outcome_status_dropdown_option_id: providerCall.outcome_status_dropdown_option_id || null,
                          last_error_message: providerCall.last_error_message || null,
                          processing_status_dropdown_option_id: providerCall.processing_status_dropdown_option_id || null,
                          processing_status_ddo: providerCall.processing_status_ddo || null,
                          bank_account_id: item.customer_bank_account_id || item.bank_account_id,
                          customer_id: item.customer_id,
                          customer_label: item.customer_label || item.customer_name,
                          customer_name: item.customer_name || item.customer_label,
                          mgr_approval_status_dropdown_option_id: managerApproval.mgr_approval_status_dropdown_option_id || null,
                          mgr_approval_status_ddo: managerApproval.mgr_approval_status_ddo || null,
                          action_reason: managerApproval.ds_response || null,
                          agent_user_id: managerApproval.agent_user_id || agentUser.id || null,
                          agent_label: agentUser.label || `${agentUser.first_name || ''} ${agentUser.last_name || ''}`.trim() || null,
                          agent_name: agentUser.label || `${agentUser.first_name || ''} ${agentUser.last_name || ''}`.trim() || null,
                          created_at: providerCall.created_at || item.created_at,
                          created_by: item.created_by || null,
                          created_by_label: item.created_by || null,
                          created_by_name: item.created_by || null
                        }
                        const status = getStatusLabel(req)
                        const providerOption = getProviderOption(req)
                        const providerLabel = providerOption?.name || '-'
                        const resultsPath = providerOption?.code_key === 'provider_chirp' ? `/chirp_results/${req.id}` : `/decision_logic_results/${req.id}`
                        const decisionLogicLink = `${window.location.origin}${resultsPath}`
                        const dlProcessingStatus = getDLProcessingStatus(req)
                        const canViewResults = ['Approved', 'Declined', 'Completed'].includes(dlProcessingStatus)

                        return (
                          <tr key={req.id}>
                            <td>
                              {canViewResults ? (
                                <button
                                  key={`view-${req.id}`}
                                  className="button is-primary"
                                >
                                  <IconWithHelperText
                                    icon={faBinoculars}
                                    helperText={'Bank Transaction'}
                                    onClick={() => window.open(resultsPath, '_blank', 'noopener,noreferrer')}
                                  />
                                </button>
                              ) : (
                                <div className="has-text-centered">-</div>
                              )}
                            </td>
                            <td className="has-text-centered">{req.id}</td>
                            <td className="has-text-centered">
                              {req.customer_id ? (
                                <Link
                                  to={`/customer/${req.customer_id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="is-link"
                                >
                                  {req.customer_label || req.customer_name || `Customer ${req.customer_id}`}
                                </Link>
                              ) : '-'}
                            </td>
                            <td className="has-text-centered">{req.ext_request_code || '-'}</td>
                            <td className="has-text-centered">
                              {providerOption ? (
                                <span className={`tag ${providerOption.code_key === 'provider_chirp' ? 'is-warning' : 'is-info'} is-light`}>
                                  {providerLabel}
                                </span>
                              ) : '-'}
                            </td>
                            <td className="has-text-centered">
                              <span className="is-inline-flex is-align-items-center">
                                {(req.mgr_approval_status_ddo || req.mgr_approval_status_dropdown_option_id) && dropdownOptions ? (
                                  <>
                                    <ManagerApprovalIcons
                                      type={req.mgr_approval_status_ddo?.code_key ||
                                            Object.values(dropdownOptions).find((opt) => opt?.id === req.mgr_approval_status_dropdown_option_id)?.code_key}
                                      tooltipDirection="bottom"
                                      row={req}
                                      dropdownOptions={dropdownOptions}
                                    />
                                    <span className="pl-2">{status}</span>
                                  </>
                                ) : (
                                  status
                                )}
                              </span>
                            </td>
                            <td className="has-text-centered">{req.action_reason || '-'}</td>
                            <td className="has-text-centered">
                                <span className="is-inline-flex is-align-items-center">
                                  {dlProcessingStatus}
                                  {req.last_error_message && (
                                    <span
                                      className={`lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-bottom has-tooltip-primary has-text-weight-normal ml-1`}
                                      data-tooltip={req.last_error_message}
                                      onMouseEnter={(e) => handleToolTipMouseEnter(e, '.table-container')}
                                    >
                                      <FontAwesomeIcon
                                        icon={faCircleInfo}
                                        className={`pl-1 has-text-link`}
                                      />
                                    </span>
                                  )}
                                </span>
                            </td>
                            <td className="has-text-centered">
                              {req.agent_user_id ? (
                                <Link
                                  to={`/user/${req.agent_user_id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="is-link"
                                >
                                  {req.agent_label || req.agent_name || `User ${req.agent_user_id}`}
                                </Link>
                              ) : '-'}
                            </td>
                            <td className="has-text-centered">{FormatISODateTime(req.created_at)}</td>
                            <td className="has-text-centered">
                              {req.created_by_label || req.created_by_name || '-'}
                            </td>
                            <td>
                              <div
                                className="buttons is-centered is-flex is-justify-content-space-evenly is-align-items-center"
                              >
                                {renderActions(req, status, canViewResults, decisionLogicLink, resultsPath)}
                              </div>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>

                  {moduleData.meta && Object.keys(moduleData.meta).length > 0 && moduleData.meta.last_page !== 1 && (
                    <Pagination
                      meta={moduleData.meta}
                      setPartialLoad={setPartialLoad}
                      baseName={module.code_key}
                    />
                  )}
                </>
              )}
            </div>
          )}
        </>
      )}
    />
  )
}
